import { Button, ConfigProvider, Flex, Modal, Radio, Typography } from "antd";
import { ModalHeader, modalButtonsTheme, modalSubtitleStyle } from "components/modals/ModalChrome";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { translate, translateWithVars } from "shared/languages";
import { BOOKING_STATUS_LABELS, BOOKING_STATUS_TONE } from "shared/utils";
import { colors } from "shared/theme/color";
import {
  BookingStatus,
  CustomerPackage,
  WindowConflict,
  WindowConflictChoice,
} from "shared/types";
import { localTzDate } from "shared/utils/dayjs";
import { formatClassInstant, formatDayKey } from "shared/utils/windowConflict";
import { useState } from "react";
import type { CSSProperties } from "react";
import { textPx } from "shared/constants/zoom";

/**
 * The bookings a choice or an end-date edit would cancel. Each is its own row
 * (a divider between rows, one soft-filled frame, so it never reads like the
 * outlined option cards it can sit above): a white date tile first, so two
 * classes read apart at a glance, then the class, its weekday and time, and
 * who the booking is for. On a shared package a booking can belong to a
 * co-sharer rather than the client being booked, so every row names its
 * client (Andrew, 2026-09-28). An older server sends no name. A long list
 * scrolls inside the frame rather than stretching the pop-up.
 */
export const AffectedBookingList = ({
  bookings,
  style,
}: {
  bookings: {
    id: string;
    status: string;
    class_name: string;
    occurrence_timestamp: string;
    client_name?: string | null;
  }[];
  style?: CSSProperties;
}) => (
  <div
    style={{
      background: colors.bgSoft,
      borderRadius: 12,
      maxHeight: 290,
      overflowY: "auto",
      overscrollBehavior: "contain",
      ...style,
    }}
  >
    {bookings.map((b, i) => {
      const at = localTzDate(b.occurrence_timestamp);
      return (
        <Flex
          key={b.id}
          align="center"
          gap={12}
          style={{
            padding: "10px 12px",
            borderTop: i === 0 ? undefined : `1px solid ${colors.lineStrong}`,
          }}
        >
          <Flex
            vertical
            align="center"
            justify="center"
            style={{
              width: 44,
              minWidth: 44,
              height: 44,
              borderRadius: 8,
              background: colors.card,
            }}
          >
            <span style={{ fontSize: textPx(16), fontWeight: 600, lineHeight: 1.1, color: colors.ink }}>
              {at.format("DD")}
            </span>
            <span
              style={{
                fontSize: textPx(10.5),
                fontWeight: 600,
                lineHeight: 1.2,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: colors.ink2,
              }}
            >
              {at.format("MMM")}
            </span>
          </Flex>
          <Flex vertical gap={2} style={{ minWidth: 0, flex: 1 }}>
            <Typography.Text ellipsis style={{ fontSize: textPx(13.5), fontWeight: 500, color: colors.ink }}>
              {b.class_name}
            </Typography.Text>
            <Typography.Text ellipsis style={{ fontSize: textPx(12.5), color: colors.ink2 }}>
              {at.format("ddd, HH:mm")}
              {b.client_name ? ` · ${b.client_name}` : ""}
            </Typography.Text>
          </Flex>
          {b.status === BookingStatus.Waitlist && (
            <StatusBadge
              tone={BOOKING_STATUS_TONE[BookingStatus.Waitlist]}
              label={BOOKING_STATUS_LABELS[BookingStatus.Waitlist]}
            />
          )}
        </Flex>
      );
    })}
  </div>
);

/** One option's text inside its card: what happens, then the consequence. */
const ChoiceText = ({
  title,
  description,
  disabled,
}: {
  title: string;
  description: string;
  disabled?: boolean;
}) => (
  <Flex vertical gap={2}>
    <Typography.Text style={{ fontWeight: 500, color: disabled ? colors.ink3 : colors.ink }}>
      {title}
    </Typography.Text>
    <Typography.Text style={{ fontSize: textPx(13), color: colors.ink2 }}>{description}</Typography.Text>
  </Flex>
);

/**
 * A booking, waitlist join or undo that would end a first-class package
 * before a class already on it (Andrew, 2026-09-28). Instead of a dead end
 * the admin chooses, at this moment:
 *  - swap: cancel the bookings that would fall outside and go on;
 *  - extend: keep the package's current end date, locked until someone
 *    edits it by hand.
 * The server does either in the same transaction as the write itself.
 *
 * The two ways through are option cards (.choice-cards in index.css, the
 * delete-package voucher choice's idiom): the whole card is the radio's
 * label, nothing is preselected, and Lanjut (navy for both choices,
 * Andrew 2026-09-28) stays disabled until one is picked.
 */
const WindowConflictDialog = ({
  conflict,
  onChoose,
  onCancel,
}: {
  conflict: WindowConflict;
  onChoose: (choice: WindowConflictChoice) => void;
  onCancel: () => void;
}) => {
  const [choice, setChoice] = useState<WindowConflictChoice | null>(null);
  const newEnd = formatDayKey(conflict.new_end);
  const currentEnd = formatDayKey(conflict.current_end);
  const only = conflict.stranded.length === 1 ? conflict.stranded[0] : null;
  return (
    <Modal
      open
      width={480}
      onCancel={onCancel}
      title={<ModalHeader tone="amber" title={translate("booking_conflict.title")} />}
      footer={
        <ConfigProvider theme={modalButtonsTheme}>
          <Flex justify="flex-end" gap={10} style={{ marginTop: 22 }}>
            <Button onClick={onCancel}>{translate("button.cancel")}</Button>
            <Button
              type="primary"
              disabled={!choice}
              onClick={() => choice && onChoose(choice)}
            >
              {translate("button.continue")}
            </Button>
          </Flex>
        </ConfigProvider>
      }
    >
      <Typography.Text style={modalSubtitleStyle}>
        {translateWithVars("booking_conflict.body", {
          package: conflict.package_name,
          date: newEnd,
        })}
      </Typography.Text>
      <AffectedBookingList bookings={conflict.stranded} style={{ margin: "12px 0 18px" }} />
      <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
        {translate("booking_conflict.choose")}
      </Typography.Text>
      <Radio.Group
        className="choice-cards"
        value={choice}
        onChange={(e) => setChoice(e.target.value)}
        style={{ width: "100%" }}
      >
        <Flex vertical gap={8}>
          <Radio value="swap" disabled={!conflict.can_swap}>
            <ChoiceText
              disabled={!conflict.can_swap}
              title={
                only
                  ? translateWithVars("booking_conflict.swap_one", {
                      class: only.class_name,
                      date: formatClassInstant(only.occurrence_timestamp),
                    })
                  : translateWithVars("booking_conflict.swap_many", {
                      n: conflict.stranded.length,
                    })
              }
              description={
                !conflict.can_swap
                  ? translate("booking_conflict.swap_unavailable")
                  : translateWithVars(
                      only ? "booking_conflict.swap_desc" : "booking_conflict.swap_desc_many",
                      { date: newEnd },
                    )
              }
            />
          </Radio>
          <Radio value="extend" disabled={!conflict.current_end}>
            <ChoiceText
              disabled={!conflict.current_end}
              title={translateWithVars("booking_conflict.extend", { date: currentEnd })}
              description={translateWithVars("booking_conflict.extend_desc", { date: currentEnd })}
            />
          </Radio>
        </Flex>
      </Radio.Group>
    </Modal>
  );
};

// Keyed on the conflict so a new one always opens with nothing picked.
const WindowConflictModal = ({
  conflict,
  onChoose,
  onCancel,
}: {
  conflict: WindowConflict | null;
  onChoose: (choice: WindowConflictChoice) => void;
  onCancel: () => void;
}) =>
  conflict ? (
    <WindowConflictDialog
      key={`${conflict.customer_package_id}:${conflict.stranded.map((b) => b.id).join(",")}`}
      conflict={conflict}
      onChoose={onChoose}
      onCancel={onCancel}
    />
  ) : null;

/**
 * The note under a picker option the class would cut short (the server's
 * window_conflict): booking with it opens the choice above.
 */
export const WindowConflictHint = ({ pkg }: { pkg: CustomerPackage }) =>
  pkg.window_conflict ? (
    <div style={{ fontSize: textPx(12), color: colors.amberDark, whiteSpace: "normal" }}>
      {translateWithVars("booking_conflict.option_hint", {
        date: formatDayKey(pkg.booking_window?.last),
      })}
    </div>
  ) : null;

export default WindowConflictModal;
